/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaWorkspace.ts
 * FILE_ID: JOBHUB-QA-WORKSPACE-HOOK-001
 * PURPOSE: Own inspection/area selection, queries, and execution mutations.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, type QueryClient } from '@tanstack/react-query'
import type {
  QaApi,
  QaAreaExecution,
  QaBulkAssignBody,
  QaFloor,
  QaInspectionSummary,
  QaItemStatus,
} from '../../qa/qaApi'
import { checklistGroups, normalizeList } from '../../qa/qaApiHelpers'
import { requestQaPhoneInspection } from '../../qa/phonePage'
import { nextQaItemStatus, pickDefaultInspection, qaErrorMessage } from './qaTabSelectors'
import { useQaWorkspaceUrl } from './useQaWorkspaceUrl'
import { QA_AREA_EXECS_KEY, qaBulkExecutionsLive, useQaAreaExecutionsWarm } from './useQaAreaExecutionsWarm'
import { currentWriteGeneration, keepCachedArea } from './qaExecutionCache'
import type { QaAreaMode } from '../JobQaModeToggle'

export function invalidateQaExecution(queryClient: QueryClient, jobId: number) {
  // With the bulk read live, one request refills every area. Refetching each
  // area key here as well was one request per area after every QA write.
  const bulkLive = qaBulkExecutionsLive(queryClient)
  queryClient.invalidateQueries({ queryKey: ['qa-area-exec'], refetchType: bulkLive ? 'none' : 'active' })
  queryClient.invalidateQueries({ queryKey: [QA_AREA_EXECS_KEY] })
  queryClient.invalidateQueries({ queryKey: ['qa-areas'] })
  queryClient.invalidateQueries({ queryKey: ['qa-floors'] })
  queryClient.invalidateQueries({ queryKey: ['qa-inspections', jobId] })
}

export function useQaWorkspace({
  api,
  jobId,
  focusInspectionId,
  queryClient,
  setError,
  canBuild = true,
  openInRun = false,
  pickAreaFirst = false,
}: {
  api: QaApi
  jobId: number
  focusInspectionId?: string | null
  queryClient: QueryClient
  setError: (error: string | null) => void
  /** False while capabilities load, error, or the account cannot build. */
  canBuild?: boolean
  /** Phone: every QA, including a just-created one, opens in Run. */
  openInRun?: boolean
  /** Phone: leave the location unset until the user picks one. */
  pickAreaFirst?: boolean
}) {
  const { initial: urlStart, write: writeUrl } = useQaWorkspaceUrl()
  const [selectedInspectionId, setSelectedInspectionId] = useState<string | null>(urlStart.qaList)
  const [selectedAreaId, setSelectedAreaId] = useState<string | null>(urlStart.qaArea)
  const [activeChecklistId, setActiveChecklistId] = useState<string | null>(urlStart.qaChecklist)
  const [allChecklists, setAllChecklists] = useState(urlStart.qaAll)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [areaMode, setAreaMode] = useState<QaAreaMode>(openInRun ? 'run' : (urlStart.qaMode ?? 'build'))
  const [leavingBuild, setLeavingBuild] = useState(false)
  // Show Run while Build is closed. Leave the stored mode alone so a builder
  // is not stuck on Run after the capabilities query resolves.
  const shownMode: QaAreaMode = !canBuild && areaMode === 'build' ? 'run' : areaMode
  // A URL with no qaMode must not paint Build and then save that paint into the URL.
  const [checklistReady, setChecklistReady] = useState(openInRun || Boolean(urlStart.qaMode) || !jobId)
  const modeDecided = useRef(openInRun || Boolean(urlStart.qaMode) || !jobId)
  const selectedRef = useRef(selectedInspectionId)
  useEffect(() => {
    selectedRef.current = selectedInspectionId
  }, [selectedInspectionId])
  const focusApplied = useRef(false)

  useEffect(() => {
    if (!focusInspectionId) return
    // The route catching up to a QA this workspace just opened must not wipe
    // the area and checklist that create already chose.
    if (focusApplied.current && selectedRef.current === focusInspectionId) return
    focusApplied.current = true
    setSelectedInspectionId(focusInspectionId)
    setSelectedAreaId(null)
    setActiveChecklistId(null)
  }, [focusInspectionId])

  const inspectionsQuery = useQuery({
    queryKey: ['qa-inspections', jobId],
    queryFn: () => api.listInspections(jobId),
    enabled: !!jobId,
  })
  const inspections = useMemo(() => inspectionsQuery.data ?? [], [inspectionsQuery.data])
  const ensured = useRef(false)
  useEffect(() => {
    if (!inspectionsQuery.isSuccess || (inspectionsQuery.data?.length ?? 0) > 0) return
    if (ensured.current || !jobId) return
    ensured.current = true
    void api.insertBlank({
      job_id: jobId,
      area_names: ['Area'],
      inspection_name: `${jobId}_QA`,
      only_if_empty: true,
    }).then(() => {
      void queryClient.invalidateQueries({ queryKey: ['qa-inspections', jobId] })
    })
  }, [api, inspectionsQuery.data, inspectionsQuery.isSuccess, jobId, queryClient])
  const activeInspectionId = selectedInspectionId ?? pickDefaultInspection(inspections)?.id ?? null

  // `select` normalizes on every READ, not just our own fetch: apps/qa shares
  // these two cache keys, so whoever fetched last decides what sits in the
  // cache. Nine consumers map/filter this data — one guard here beats nine.
  const areasQuery = useQuery({
    queryKey: ['qa-areas', activeInspectionId],
    queryFn: () => api.listAreas(activeInspectionId as string),
    select: (data) => normalizeList<QaAreaExecution>(data),
    enabled: !!activeInspectionId,
  })
  // Levels are fetched, not derived from the areas: a Level the user emptied
  // by moving its last Location away still has to appear so they can move
  // something back into it.
  const floorsQuery = useQuery({
    queryKey: ['qa-floors', activeInspectionId],
    queryFn: () => api.listFloors(activeInspectionId as string),
    select: (data) => normalizeList<QaFloor>(data),
    enabled: !!activeInspectionId,
  })
  const activeAreaId = selectedAreaId ?? (pickAreaFirst ? null : areasQuery.data?.[0]?.id) ?? null
  const areaExecQuery = useQuery({
    queryKey: ['qa-area-exec', activeAreaId],
    queryFn: async () => {
      const id = activeAreaId as string
      const started = currentWriteGeneration(id)
      // Reused from: packages/job-hub/src/jobs/tabs/job-qa/qaExecutionCache.ts (keepCachedArea)
      const fetched = await api.getAreaExecution(id, { photos: false })
      return keepCachedArea(id, started, fetched, queryClient.getQueryData(['qa-area-exec', id]))
    },
    enabled: !!activeAreaId,
  })

  const areaExecutionsWarm = useQaAreaExecutionsWarm({
    api,
    inspectionId: activeInspectionId,
    areaIds: (areasQuery.data ?? []).map((area) => area.id),
    queryClient,
  })

  const selectInspection = (inspectionId: string | null) => {
    setSelectedInspectionId(inspectionId)
    setSelectedAreaId(null)
    setActiveChecklistId(null)
  }
  // A new QA joins this job's list now, and the QA hub record route follows it.
  // Reused from: packages/job-hub/src/jobs/qa/phonePage.ts (requestQaPhoneInspection)
  const openCreatedInspection = (inspectionId: string) => {
    selectInspection(inspectionId)
    if (openInRun) setAreaMode('run')
    queryClient.invalidateQueries({ queryKey: ['qa-inspections', jobId] })
    requestQaPhoneInspection(inspectionId)
  }
  const leaveBuild = async (next: 'run' | 'itp' = 'run') => {
    setLeavingBuild(true)
    const started = Date.now()
    // ponytail: polls the mutation count; ceiling 10s, then refreshes anyway. Upgrade path: await tracked write promises.
    while (queryClient.isMutating() > 0 && Date.now() - started < 10_000) {
      await new Promise((resolve) => window.setTimeout(resolve, 100))
    }
    // Reused from: useQaWorkspace.ts (invalidateQaExecution, setAreaMode)
    // Keys Run and ITP read: qa-area-exec, qa-areas, qa-floors, qa-inspections, qa-record-settings, qa-reports, qa-participants.
    invalidateQaExecution(queryClient, jobId)
    queryClient.invalidateQueries({ queryKey: ['qa-record-settings'] })
    queryClient.invalidateQueries({ queryKey: ['qa-reports'] })
    queryClient.invalidateQueries({ queryKey: ['qa-participants'] })
    setAreaMode(next)
    setLeavingBuild(false)
  }
  const selectArea = (areaId: string | null) => {
    setSelectedAreaId(areaId)
    setActiveChecklistId(null)
  }
  // Follow a ticked checklist or check into its own area without dropping the checklist (B1).
  const focusArea = (areaId: string, checklistId?: string | null) => {
    setSelectedAreaId(areaId)
    setActiveChecklistId(checklistId ?? null)
    if (checklistId) setAllChecklists(false)
  }
  const invalidateExecution = () => {
    if (!activeAreaId) return
    queryClient.invalidateQueries({ queryKey: ['qa-area-exec', activeAreaId], exact: true })
  }
  // The floors query nests the same areas, so anything that changes an area
  // must refresh both or the rail shows the Location's old name under its Level.
  const invalidateAreas = () => {
    queryClient.invalidateQueries({ queryKey: ['qa-areas', activeInspectionId] })
    queryClient.invalidateQueries({ queryKey: ['qa-floors', activeInspectionId] })
    queryClient.invalidateQueries({ queryKey: ['qa-inspections', jobId] })
  }

  const remove = useMutation({
    mutationFn: (inspectionId: string) => api.deleteInspection(inspectionId),
    onMutate: async (inspectionId) => {
      setConfirmDeleteId(null)
      setSelectedInspectionId((current) => (current === inspectionId ? null : current))
      setSelectedAreaId(null)
      await queryClient.cancelQueries({ queryKey: ['qa-inspections', jobId] })
      const previous = queryClient.getQueryData<QaInspectionSummary[]>(['qa-inspections', jobId])
      queryClient.setQueryData<QaInspectionSummary[]>(
        ['qa-inspections', jobId],
        (rows) => rows?.filter((row) => row.id !== inspectionId),
      )
      return { previous }
    },
    onError: (error: unknown, _inspectionId, context) => {
      if (context?.previous) queryClient.setQueryData(['qa-inspections', jobId], context.previous)
      setError(qaErrorMessage(error, 'Failed to delete the QA list.'))
    },
    onSettled: () => invalidateQaExecution(queryClient, jobId),
  })
  const setStatus = useMutation({
    mutationFn: ({ itemId, status }: { itemId: string; status: QaItemStatus }) =>
      api.setItemStatus(itemId, status),
    onSuccess: (item) => {
      setError(null)
      if (activeAreaId) {
        queryClient.setQueryData(['qa-area-exec', activeAreaId], (current: QaAreaExecution | undefined) => (
          current ? { ...current, checklists: current.checklists.map((checklist) => ({
            ...checklist,
            items: checklist.items.map((row) => (row.id === item.id ? { ...row, ...item } : row)),
          })) } : current
        ))
      }
      queryClient.invalidateQueries({ queryKey: ['qa-inspections', jobId], exact: true })
    },
    onError: (error: unknown) => setError(qaErrorMessage(error, 'Could not save that status.')),
  })
  const renameInspection = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.renameInspection(id, name),
    onSuccess: () => setError(null),
    onError: (error: unknown) => setError(qaErrorMessage(error, 'Failed to rename the QA list.')),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['qa-inspections', jobId] }),
  })
  const duplicateInspection = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string
      body?: { inspection_name?: string; job_id?: string }
    }) => api.duplicateInspection(id, body ?? {}),
    onSuccess: (created) => {
      setError(null)
      const createdJob = created.job_id
      if (createdJob == null || String(createdJob) === String(jobId)) {
        selectInspection(created.id)
      }
    },
    onError: (error: unknown) => setError(qaErrorMessage(error, 'Failed to duplicate the QA list.')),
    onSettled: () => invalidateQaExecution(queryClient, jobId),
  })
  const assignInspectionGroup = useMutation({
    mutationFn: (body: {
      inspection_ids: string[]
      job_id: string
      group_id?: string | null
      group_name?: string
    }) => api.assignInspectionGroup(body),
    onSuccess: () => setError(null),
    onError: (error: unknown) => setError(qaErrorMessage(error, 'Failed to move the QA list.')),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['qa-inspections', jobId] }),
  })
  const renameArea = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.renameArea(id, name),
    onSuccess: (area) => {
      setError(null)
      queryClient.setQueryData(['qa-areas', activeInspectionId], (current: unknown) => (
        Array.isArray(current)
          ? current.map((row) => (row?.id === area.id ? { ...row, area_name: area.area_name } : row))
          : current
      ))
      queryClient.setQueryData(['qa-floors', activeInspectionId], (current: unknown) => (
        Array.isArray(current)
          ? current.map((floor) => ({
            ...floor,
            areas: Array.isArray(floor?.areas)
              ? floor.areas.map((row) => (row?.id === area.id ? { ...row, area_name: area.area_name } : row))
              : floor?.areas,
          }))
          : current
      ))
      queryClient.setQueryData(['qa-area-exec', area.id], (current: QaAreaExecution | undefined) => (
        current ? { ...current, area_name: area.area_name } : current
      ))
    },
    onError: (error: unknown) => setError(qaErrorMessage(error, 'Failed to rename the area.')),
  })
  const addTable = useMutation({
    mutationFn: (input?: { seedDefaultItems?: boolean }) => api.addTable(
      activeInspectionId as string,
      'New table',
      input?.seedDefaultItems !== false,
    ),
    onSuccess: (area) => {
      setError(null)
      setSelectedAreaId(area.id)
      setActiveChecklistId(null)
      queryClient.setQueryData(['qa-areas', activeInspectionId], (current: unknown) => {
        if (!Array.isArray(current)) return current
        if (current.some((row) => row?.id === area.id)) return current
        return [...current, area]
      })
      queryClient.setQueryData(['qa-floors', activeInspectionId], (current: unknown) => {
        if (!Array.isArray(current) || current.length === 0) return current
        const [first, ...rest] = current
        const nested = Array.isArray(first?.areas) ? first.areas : []
        if (nested.some((row) => row?.id === area.id)) return current
        return [{ ...first, areas: [...nested, area] }, ...rest]
      })
    },
    onError: (error: unknown) => setError(qaErrorMessage(error, 'Failed to add a table.')),
  })

  const activeInspection = useMemo(
    () => inspections.find((inspection) => inspection.id === activeInspectionId) ?? null,
    [inspections, activeInspectionId],
  )
  const isDraft = (activeInspection?.status ?? 'draft') === 'draft'
  const lastInspectionRef = useRef<string | null>(null)
  useEffect(() => {
    if (modeDecided.current) return
    if (!inspectionsQuery.isSuccess) return
    modeDecided.current = true
    setAreaMode(isDraft ? 'build' : 'run')
    setChecklistReady(true)
  }, [inspectionsQuery.isSuccess, isDraft])
  useEffect(() => {
    if (!activeInspectionId || activeInspectionId === lastInspectionRef.current) return
    if (openInRun) {
      lastInspectionRef.current = activeInspectionId
      setAreaMode('run')
      return
    }
    if (!modeDecided.current || urlStart.qaMode) {
      lastInspectionRef.current = activeInspectionId
      return
    }
    lastInspectionRef.current = activeInspectionId
    setAreaMode(isDraft ? 'build' : 'run')
  }, [activeInspectionId, isDraft, urlStart.qaMode, openInRun])

  const checklistIds = useMemo(() => {
    const data = areaExecQuery.data
    // Never pick a checklist from another area's payload (the old placeholder did).
    if (!data || data.id !== activeAreaId) return []
    return checklistGroups(data, undefined)
      .map((group) => group.checklist.id)
  }, [areaExecQuery.data, activeAreaId, shownMode])
  const pendingPick = useRef<{ id: string; at: number } | null>(null)
  const chooseActiveChecklist = (id: string | null) => {
    if (id) pendingPick.current = { id, at: Date.now() }
    setActiveChecklistId(id)
  }
  useEffect(() => {
    if (allChecklists || checklistIds.length === 0) return
    const pending = pendingPick.current
    if (pending && activeChecklistId === pending.id) {
      if (checklistIds.includes(pending.id)) pendingPick.current = null
      else if (areaExecQuery.dataUpdatedAt <= pending.at) return
      else pendingPick.current = null
    }
    if (!activeChecklistId || !checklistIds.includes(activeChecklistId)) {
      setActiveChecklistId(checklistIds[0])
    }
  }, [allChecklists, checklistIds, activeChecklistId, areaExecQuery.dataUpdatedAt])

  useEffect(() => {
    if (!checklistReady) return
    writeUrl({
      qaList: activeInspectionId,
      qaArea: activeAreaId,
      qaChecklist: activeChecklistId,
      qaMode: areaMode,
      qaAll: allChecklists,
    })
  }, [checklistReady, activeInspectionId, activeAreaId, activeChecklistId, areaMode, allChecklists, writeUrl])

  // Kept alongside the explicit picker below: other surfaces still call it, and
  // one tap through the common states is faster than opening a menu.
  const cycleStatus = (itemId: string, current: QaItemStatus) => {
    setStatus.mutate({ itemId, status: nextQaItemStatus(current) })
  }

  const setItemStatus = (itemId: string, status: QaItemStatus) => {
    setStatus.mutate({ itemId, status })
  }

  const setAreaWorkStatus = useMutation({
    mutationFn: ({ areaId, status }: { areaId: string; status: QaItemStatus }) =>
      api.setAreaWorkStatus(areaId, status),
    onSuccess: () => {
      setError(null)
      // Both keys: the area list shows the badge, the execution panel shows the menu.
      invalidateAreas()
      queryClient.invalidateQueries({ queryKey: ['qa-area-exec', activeAreaId] })
    },
    onError: (error: unknown) => setError(qaErrorMessage(error, 'Failed to update the area status.')),
  })
  const assignArea = useMutation({
    mutationFn: ({ areaId, userId }: { areaId: string; userId: number | null }) =>
      api.assignArea(areaId, userId),
    onSuccess: () => {
      setError(null)
      invalidateAreas()
      queryClient.invalidateQueries({ queryKey: ['qa-area-exec', activeAreaId] })
    },
    onError: (error: unknown) => setError(qaErrorMessage(error, 'Failed to assign the area.')),
  })
  const assignItem = useMutation({
    mutationFn: ({ itemId, userId }: { itemId: string; userId: number | null }) =>
      api.assignItem(itemId, userId),
    onSuccess: () => {
      setError(null)
      queryClient.invalidateQueries({ queryKey: ['qa-area-exec', activeAreaId] })
    },
    onError: (error: unknown) => setError(qaErrorMessage(error, 'Failed to assign the item.')),
  })
  const clearAssignedSelection = useRef<() => void>(() => {})
  const bulkAssign = useMutation({
    mutationFn: (body: QaBulkAssignBody) =>
      api.bulkAssign(activeInspectionId as string, body),
    // Same keys the single-row assigns invalidate: one bulk call can move rows
    // in the rail and in the open area at once. The selection stays until this
    // resolves, so a failed assign does not collapse the toolbar.
    onSuccess: () => {
      setError(null)
      clearAssignedSelection.current()
    },
    onError: (error: unknown) => setError(qaErrorMessage(error, 'Failed to assign the selection.')),
  })

  return {
    bulkAssign,
    rememberAssignedClear: (clear: () => void) => {
      clearAssignedSelection.current = clear
    },
    activeAreaId,
    activeChecklistId,
    allChecklists,
    activeInspectionId,
    addTable,
    api,
    assignArea,
    assignItem,
    areaExecQuery,
    areaExecutionsReady: areaExecutionsWarm.ready,
    areaMode: shownMode,
    checklistReady,
    areasQuery,
    confirmDeleteId,
    cycleStatus,
    floorsQuery,
    inspections,
    inspectionsQuery,
    invalidateExecution,
    invalidateAreas,
    remove,
    renameArea,
    renameInspection,
    duplicateInspection,
    setError,
    assignInspectionGroup,
    selectInspection,
    openCreatedInspection,
    selectArea,
    focusArea,
    selectedAreaId,
    setActiveChecklistId: chooseActiveChecklist,
    setAllChecklists,
    setAreaMode,
    leaveBuild,
    leavingBuild,
    setAreaWorkStatus,
    setItemStatus,
    setConfirmDeleteId,
    setSelectedAreaId,
    setStatus,
  }
}

export type QaWorkspaceController = ReturnType<typeof useQaWorkspace>
